import type {
  UrlContextSource,
  UrlManagedParamConfig,
} from '@/config/shared/url-context';

import type { UrlContextStore } from './UrlContextStore';
import {
  buildSearchString,
  generatePublicId,
  isBrowser,
  parseSearchParams,
  silentReplaceState,
} from './utils';

export type ManagedParamResolution = {
  values: Record<string, string>;
  sources: Record<string, UrlContextSource>;
  decorateParams: Record<string, string>;
};

/**
 * Resolve managed params: URL → cookie → storage → generated.
 * Persiste e opcionalmente escreve na URL via replaceState silencioso.
 */
export class ManagedParamResolver {
  private readonly store: UrlContextStore;

  constructor(store: UrlContextStore) {
    this.store = store;
  }

  resolve(configs: readonly UrlManagedParamConfig[]): ManagedParamResolution {
    const values: Record<string, string> = {};
    const sources: Record<string, UrlContextSource> = {};
    const decorateParams: Record<string, string> = {};
    const urlWrites: Record<string, string> = {};

    const currentParams = isBrowser()
      ? parseSearchParams(window.location.search)
      : {};

    for (const config of configs) {
      const param = config.param?.trim();
      if (!param) continue;

      const mode = config.mode ?? 'read_write';
      const canRead = mode === 'read_only' || mode === 'read_write';
      const canWrite = mode === 'write_only' || mode === 'read_write';

      let value: string | null = null;
      let source: UrlContextSource | null = null;

      if (canRead) {
        const fromUrl = currentParams[param];
        if (fromUrl && fromUrl.length > 0) {
          value = fromUrl;
          source = 'url';
        }
      }

      if (!value && canRead && config.persistence?.enabled !== false) {
        const cookieName = config.persistence?.cookie?.name?.trim() || param;
        if (config.persistence?.cookie?.enabled !== false) {
          const fromCookie = this.store.getCookie(cookieName);
          if (fromCookie) {
            value = fromCookie;
            source = 'cookie';
          }
        }
      }

      if (!value && canRead && config.persistence?.enabled !== false) {
        const storageKey = config.persistence?.storageKey?.trim() || param;
        const fromStorage = this.store.getStorage(storageKey);
        if (fromStorage) {
          value = fromStorage;
          source = 'storage';
        }
      }

      if (!value && canWrite && config.generator?.type === 'public_id') {
        value = generatePublicId(config.generator.prefix ?? param);
        source = 'generated';
      }

      if (!value || !source) continue;

      values[param] = value;
      sources[param] = source;

      if (config.decorateLinks !== false) {
        decorateParams[param] = value;
      }

      // Persist always when we have a value and persistence enabled
      if (config.persistence?.enabled !== false) {
        this.persist(config, value);
      }

      // Write to URL if strategy allows and value missing/different on URL
      if (
        canWrite &&
        (config.writeStrategy ?? 'replaceState') === 'replaceState' &&
        isBrowser()
      ) {
        const existing = currentParams[param];
        if (existing !== value) {
          urlWrites[param] = value;
        }
      }
    }

    if (Object.keys(urlWrites).length > 0 && isBrowser()) {
      this.writeToUrl(urlWrites);
    }

    return { values, sources, decorateParams };
  }

  /**
   * Aplica valor externo após o resolve inicial (sem generator).
   * Persiste e opcionalmente escreve na URL.
   */
  applyExternalValue(config: UrlManagedParamConfig, value: string): void {
    const trimmed = value.trim();
    if (!trimmed) return;

    if (config.persistence?.enabled !== false) {
      this.persist(config, trimmed);
    }

    if (
      (config.writeStrategy ?? 'replaceState') === 'replaceState' &&
      isBrowser()
    ) {
      this.writeToUrl({ [config.param]: trimmed });
    }
  }

  private persist(config: UrlManagedParamConfig, value: string): void {
    const strategy = config.persistence?.strategy ?? 'localStorage_cookie';
    const storageKey = config.persistence?.storageKey?.trim() || config.param;
    const cookieCfg = config.persistence?.cookie;
    const cookieName = cookieCfg?.name?.trim() || config.param;

    if (strategy === 'storage' || strategy === 'localStorage_cookie') {
      this.store.setStorage(storageKey, value);
    }

    if (
      (strategy === 'cookie' || strategy === 'localStorage_cookie') &&
      cookieCfg?.enabled !== false
    ) {
      this.store.setCookie({
        name: cookieName,
        value,
        path: cookieCfg?.path ?? '/',
        domain: cookieCfg?.domain ?? 'auto',
        sameSite: cookieCfg?.sameSite ?? 'Lax',
        secure: cookieCfg?.secure ?? true,
        maxAgeMs: cookieCfg?.maxAgeMs,
      });
    }
  }

  private writeToUrl(writes: Record<string, string>): void {
    const current = parseSearchParams(window.location.search);
    const next = { ...current, ...writes };
    const search = buildSearchString(next);
    const hash = window.location.hash ?? '';
    const path = window.location.pathname + search + hash;
    silentReplaceState(path);
  }
}
